/* ============================================================
   src/utils/auth.js
   EnteliPro — Shared Authentication Utility
   
   Single source of truth for token storage, retrieval, and
   auth status. Used by Login page, AI chatbot, and all API
   calls. Never read localStorage directly outside this file.
   ============================================================ */

// ── Constants ──────────────────────────────────────────────────
const TOKEN_KEY = "accessToken";
const USER_KEY = "entelipro_user";
const ADMIN_ID_KEY = "adminId";
const USER_ID_KEY = "userId";
const NAME_KEY = "name";
const EMAIL_KEY = "email";

// ── UUID Regex validator helper ───────────────────────────────
export const isUUID = (value) => {
  if (typeof value !== "string") return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value.trim());
};

// ── Clean token helper ────────────────────────────────────────
const cleanToken = (token) => {
  if (!token) return null;
  let cleaned = token.trim();

  // Remove quotes if present
  if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
    cleaned = cleaned.slice(1, -1).trim();
  }

  // Remove "Bearer " prefix if present
  if (cleaned.toLowerCase().startsWith("bearer ")) {
    cleaned = cleaned.substring(7).trim();
  }

  return cleaned || null;
};

// ── Save token after successful login ────────────────────────
export const saveToken = (token) => {
  if (!token) return;

  const clean = cleanToken(token);
  if (!clean) return;

  // Store in primary location
  localStorage.setItem(TOKEN_KEY, clean);

  // Backward compatibility - remove redundant keys
  // Only keep one primary source of truth
  // Remove old/legacy keys to prevent confusion
  const legacyKeys = ["entelipro_token", "authToken", "token", "jwt", "access_token"];
  legacyKeys.forEach(key => localStorage.removeItem(key));

  // Clear stale non-UUID IDs
  const existingAdminId = localStorage.getItem(ADMIN_ID_KEY);
  if (existingAdminId && !isUUID(existingAdminId)) {
    localStorage.removeItem(ADMIN_ID_KEY);
  }

  const existingUserId = localStorage.getItem(USER_ID_KEY);
  if (existingUserId && !isUUID(existingUserId)) {
    localStorage.removeItem(USER_ID_KEY);
  }
};

// ── Read token — used by chatbot and all API calls ────────────
export const getToken = () => {
  // Primary source
  let rawToken = localStorage.getItem(TOKEN_KEY);

  // Fallback to sessionStorage if not found (backward compatibility)
  if (!rawToken) {
    rawToken = sessionStorage.getItem(TOKEN_KEY);
  }

  // Last resort - check legacy keys (but clean them up)
  if (!rawToken) {
    const legacyKeys = ["entelipro_token", "authToken", "token", "jwt", "access_token"];
    for (const key of legacyKeys) {
      const value = localStorage.getItem(key);
      if (value) {
        rawToken = value;
        // Migrate to primary key and remove legacy
        localStorage.setItem(TOKEN_KEY, cleanToken(value));
        localStorage.removeItem(key);
        break;
      }
    }
  }

  if (!rawToken) {
    // Check sessionStorage legacy keys
    const legacyKeys = ["authToken", "token"];
    for (const key of legacyKeys) {
      const value = sessionStorage.getItem(key);
      if (value) {
        rawToken = value;
        // Migrate to primary key
        localStorage.setItem(TOKEN_KEY, cleanToken(value));
        sessionStorage.removeItem(key);
        break;
      }
    }
  }

  return rawToken ? cleanToken(rawToken) : null;
};

// ── Remove token on logout ────────────────────────────────────
export const removeToken = () => {
  // Remove all token storage locations
  const allKeys = [
    TOKEN_KEY,
    "entelipro_token",
    "authToken",
    "token",
    "jwt",
    "access_token",
    USER_KEY,
    USER_ID_KEY,
    ADMIN_ID_KEY,
    NAME_KEY,
    EMAIL_KEY,
    "entelipro_user",
    "userName",
    "status"
  ];

  allKeys.forEach(key => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  // Clean up any other potential keys
  localStorage.removeItem("name");
  localStorage.removeItem("userName");
  localStorage.removeItem("userId");
  localStorage.removeItem("adminId");
};

export const clearToken = removeToken;

// ── Check if the user is currently authenticated ──────────────
export const isLoggedIn = () => {
  const token = getToken();
  if (!token) return false;

  // Decode JWT and check expiry
  try {
    const parts = token.split(".");
    if (parts.length === 3) {
      const payload = JSON.parse(atob(parts[1]));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        removeToken();
        return false;
      }
    }
  } catch (_) {
    // Token is opaque or non-JWT — treat as valid until backend rejects it
    // We'll still check if token exists
    return true;
  }

  return true;
};

export const isUserLoggedIn = isLoggedIn;

// ── Build Authorization header ready for fetch / axios ────────
export const getAuthHeader = () => {
  const token = getToken();
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
  };
};

// ── Fire login event — notifies entire app including chatbot ──
export const notifyLogin = (token) => {
  if (token) {
    saveToken(token);
  }

  const currentToken = getToken();
  const detail = { token: currentToken };

  // Dispatch multiple event types for maximum compatibility
  window.dispatchEvent(new CustomEvent("entelipro:login", { detail }));
  window.dispatchEvent(new CustomEvent("entelipro-login", { detail }));
  window.dispatchEvent(new CustomEvent("user-logged-in", { detail }));

  // Also dispatch old-style event for backward compatibility
  const event = new Event("user-logged-in");
  window.dispatchEvent(event);
};

// ── Fire logout event — notifies entire app ───────────────────
export const notifyLogout = () => {
  removeToken();
  localStorage.removeItem("name");
  localStorage.removeItem("userName");
  localStorage.removeItem("userId");
  localStorage.removeItem("adminId");

  // Dispatch multiple event types for maximum compatibility
  window.dispatchEvent(new Event("entelipro:logout"));
  window.dispatchEvent(new Event("entelipro-logout"));
  window.dispatchEvent(new Event("user-logged-out"));
};

// ── Decode JWT token payload ─────────────────────────────────
export const decodeTokenPayload = () => {
  const token = getToken();
  if (!token) return null;

  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Failed to decode token payload:", e);
    return null;
  }
};

// ── Save user info ───────────────────────────────────────────
export const saveUserInfo = (userInfo) => {
  if (!userInfo) return;

  const info = { ...userInfo };

  // Handle different ID field names
  if (info.id) {
    localStorage.setItem(USER_ID_KEY, info.id);
  } else if (info.userId) {
    localStorage.setItem(USER_ID_KEY, info.userId);
    info.id = info.userId;
  }

  if (info.useradminid || info.adminid) {
    const adminId = info.useradminid || info.adminid;
    localStorage.setItem(ADMIN_ID_KEY, adminId);
  }

  if (info.name) {
    localStorage.setItem(NAME_KEY, info.name);
  }

  if (info.email) {
    localStorage.setItem(EMAIL_KEY, info.email);
  }

  // Save full user object
  localStorage.setItem(USER_KEY, JSON.stringify(info));
};

// ── Read user info ───────────────────────────────────────────
export const getUserInfo = () => {
  try {
    const storedUser = localStorage.getItem(USER_KEY);
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      if (parsed && Object.keys(parsed).length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.error("Failed to parse user info:", e);
  }

  // Fallback: reconstruct from individual keys
  const id = localStorage.getItem(USER_ID_KEY);
  const useradminid = localStorage.getItem(ADMIN_ID_KEY);
  const name = localStorage.getItem(NAME_KEY);
  const email = localStorage.getItem(EMAIL_KEY);

  if (id || useradminid || name) {
    return { id, useradminid, name, email };
  }

  return null;
};

// ── Get user ID helper ──────────────────────────────────────
export const getUserId = () => {
  const userInfo = getUserInfo();
  return userInfo?.id || localStorage.getItem(USER_ID_KEY) || null;
};

// ── Get admin ID helper ─────────────────────────────────────
export const getAdminId = () => {
  const userInfo = getUserInfo();
  return userInfo?.useradminid || userInfo?.adminid || localStorage.getItem(ADMIN_ID_KEY) || null;
};